/**
 * `useActiveOrg` — single source of truth for "which org is the
 * dashboard currently acting on".
 *
 * Clerk exposes the active org via TWO independent surfaces:
 *
 *   1. `useOrganization().organization.id` — the **live SDK** value.
 *      Updates instantly when `setActive({ organization })` is called
 *      from anywhere (sidebar switcher, `<OrganizationSwitcher>`,
 *      deep link, checkout completion).
 *
 *   2. `useAuth().orgId` — the **JWT session-claim** value. Comes
 *      from the `o.id` / `org_id` claim on the last-minted token.
 *      Lags the live SDK until Clerk rotates the token (normally
 *      within ~1s after `setActive`, but can be seconds on a stale
 *      tab).
 *
 * Every API request signs itself with the JWT, so `apps/api`
 * resolves the active org from the *claim* value — the server sees
 * whichever org id the token carries, not what `useOrganization()`
 * says. If the dashboard reads "plan" from `useOrganization()` (live
 * SDK) but calls `/v1/usage` with a token whose claim still points
 * at a different org, the two surfaces render data for two different
 * orgs. That's exactly the class of bug that causes "sidebar says
 * no active plan / billing page says Startup / Clerk says Pro /
 * Supabase (for the row Studio picked) says Pro".
 *
 * Rules this hook enforces:
 *   - There is ONE `orgId` for the dashboard: the JWT claim
 *     (`useAuth().orgId`). We pick the claim because the API is
 *     already committed to it — every fetch goes out with the
 *     current token, so the UI must agree with the server. If we
 *     trusted the live SDK we'd have to either (a) delay every
 *     fetch until the token catches up, or (b) force a token
 *     refresh on every switch. Picking the claim as the canonical
 *     id means "what the UI shows === what the server computed".
 *   - `claimOrgId` / `liveOrgId` are exposed for diagnostics and
 *     for the rare caller that needs to display pre-reload state
 *     (e.g. the org switcher itself, which wants to show "active"
 *     on the just-clicked row before the JWT rotates).
 *   - `isSynced` = `claimOrgId === liveOrgId`. When `false`, the
 *     user almost certainly just switched orgs and the token is
 *     in-flight. Pages can render a skeleton / disable write CTAs
 *     during this window.
 *   - In DEV, a desync that lasts longer than a short grace period
 *     (4s) emits a single console.warn so this class of bug is
 *     visible on first render instead of surfacing as a plan
 *     mismatch days later.
 */
import { useAuth, useOrganization } from '@clerk/react';
import { useEffect, useRef } from 'react';

export interface ActiveOrg {
  /**
   * The id every hook, query key, and API request should use.
   * Equal to `useAuth().orgId` — i.e. the JWT session-claim org.
   * `null` when signed out, or when the user is in a personal
   * account with no active org.
   */
  orgId: string | null;
  /** Same as `orgId`. Alias to read like `claimOrgId` when the
   * intent is "the claim value specifically, for diagnostics". */
  claimOrgId: string | null;
  /** Clerk SDK live value. Use this ONLY for diagnostics or for UI
   * that intentionally wants to render pre-token-rotation state
   * (e.g. the just-clicked row in the org switcher). */
  liveOrgId: string | null;
  /** `true` when the JWT claim and the live SDK agree. `false`
   * means a switch is in flight; pages should generally show a
   * loading state rather than data for either org. */
  isSynced: boolean;
  /** Both Clerk surfaces have finished their initial load. */
  isLoaded: boolean;
}

// Module-level de-dupe for the dev warning so a slow token refresh
// doesn't spam the console every re-render.
let devWarnedOnceFor: string | null = null;

export function useActiveOrg(): ActiveOrg {
  const { isLoaded: authLoaded, orgId: claimOrgId } = useAuth();
  const { isLoaded: orgLoaded, organization } = useOrganization();

  const liveOrgId = organization?.id ?? null;
  const isLoaded = authLoaded && orgLoaded;
  const isSynced = claimOrgId === liveOrgId;

  // Dev-only: warn when the mismatch persists past the token-rotation
  // grace period. A transient `isSynced === false` right after
  // `setActive` is expected; a persistent one is the bug class that
  // caused the plan inconsistency.
  const mismatchSince = useRef<number | null>(null);
  useEffect(() => {
    if (!isLoaded) return;
    if (isSynced) {
      mismatchSince.current = null;
      return;
    }
    const now = Date.now();
    if (mismatchSince.current === null) {
      mismatchSince.current = now;
      return;
    }
    const GRACE_MS = 4_000;
    if (now - mismatchSince.current < GRACE_MS) return;

    if (import.meta.env.DEV) {
      const signature = `${claimOrgId ?? 'null'}::${liveOrgId ?? 'null'}`;
      if (devWarnedOnceFor === signature) return;
      devWarnedOnceFor = signature;
      // eslint-disable-next-line no-console
      console.warn(
        `[useActiveOrg] Clerk JWT claim and live SDK org diverge. claimOrgId=${claimOrgId ?? 'null'} liveOrgId=${liveOrgId ?? 'null'}. This will cause plan/usage UIs to show data from a different org than the API is reading. Likely causes: setActive() was called but the token has not rotated yet, or the user was signed in on a tab that missed the switch. The dashboard canonicalises on the JWT claim (what the server actually sees), so the sidebar will follow.`,
      );
    }
  }, [isLoaded, isSynced, claimOrgId, liveOrgId]);

  return {
    orgId: claimOrgId ?? null,
    claimOrgId: claimOrgId ?? null,
    liveOrgId,
    isSynced,
    isLoaded,
  };
}
